import { supabase } from '../lib/supabase';


const isDev = import.meta.env?.VITE_DEV_MODE === 'true';
const mockUserId = 'dev-user-123';

// Helper to check schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

export const reviewsService = {
  // Create a new rating/review
  async createReview(reviewData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const reviewPayload = toSnakeCase({
        ...reviewData,
        fromUserId: user?.id
      });

      const { data, error } = await supabase
        ?.from('ratings')
        ?.insert(reviewPayload)
        ?.select(`
          *,
          from_user:user_profiles!ratings_from_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          to_user:user_profiles!ratings_to_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          job:jobs!ratings_job_id_fkey(
            id,
            title
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Review creation error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create review error:', error);
      throw error;
    }
  },

  // Get reviews for a specific user (received reviews)
  async getUserReviews(userId) {
    try {
      const { data, error } = await supabase
        ?.from('ratings')
        ?.select(`
          *,
          from_user:user_profiles!ratings_from_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          job:jobs!ratings_job_id_fkey(
            id,
            title
          ),
          review_responses(
            id,
            response,
            created_at,
            responder:user_profiles!review_responses_responder_id_fkey(
              id,
              full_name,
              avatar_url
            )
          )
        `)
        ?.eq('to_user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User reviews fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user reviews error:', error);
      throw error;
    }
  },

  // Get reviews written by a user
  async getReviewsByUser(userId) {
    try {
      const { data, error } = await supabase
        ?.from('ratings')
        ?.select(`
          *,
          to_user:user_profiles!ratings_to_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          job:jobs!ratings_job_id_fkey(
            id,
            title
          )
        `)
        ?.eq('from_user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Reviews by user fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get reviews by user error:', error);
      throw error;
    }
  },

  // Get reviews for a specific job
  async getJobReviews(jobId) {
    try {
      const { data, error } = await supabase
        ?.from('ratings')
        ?.select(`
          *,
          from_user:user_profiles!ratings_from_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          to_user:user_profiles!ratings_to_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          review_responses(
            id,
            response,
            created_at,
            responder:user_profiles!review_responses_responder_id_fkey(
              id,
              full_name,
              avatar_url
            )
          )
        `)
        ?.eq('job_id', jobId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Job reviews fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get job reviews error:', error);
      throw error;
    }
  },

  // Create a response to a review
  async createReviewResponse(responseData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const responsePayload = toSnakeCase({
        ...responseData,
        responderId: user?.id
      });

      const { data, error } = await supabase
        ?.from('review_responses')
        ?.insert(responsePayload)
        ?.select(`
          *,
          responder:user_profiles!review_responses_responder_id_fkey(
            id,
            full_name,
            avatar_url
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Review response creation error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create review response error:', error);
      throw error;
    }
  },

  // Update a review response
  async updateReviewResponse(responseId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const updatePayload = toSnakeCase({
        ...updates,
        updatedAt: new Date()?.toISOString()
      });

      const { data, error } = await supabase
        ?.from('review_responses')
        ?.update(updatePayload)
        ?.eq('id', responseId)
        ?.eq('responder_id', user?.id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Review response update error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update review response error:', error);
      throw error;
    }
  },

  // Delete a review response
  async deleteReviewResponse(responseId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        ?.from('review_responses')
        ?.delete()
        ?.eq('id', responseId)
        ?.eq('responder_id', user?.id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Review response deletion error:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete review response error:', error);
      throw error;
    }
  },

  // Update a review (for adding photos after creation)
  async updateReview(ratingId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const updatePayload = toSnakeCase(updates);

      const { data, error } = await supabase
        ?.from('ratings')
        ?.update(updatePayload)
        ?.eq('id', ratingId)
        ?.eq('from_user_id', user?.id)
        ?.select(`
          *,
          from_user:user_profiles!ratings_from_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          to_user:user_profiles!ratings_to_user_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          job:jobs!ratings_job_id_fkey(
            id,
            title
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Review update error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update review error:', error);
      throw error;
    }
  },

  // Upload photo for review
  async uploadReviewPhoto(file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only JPEG, PNG, WebP, and GIF are allowed.');
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file?.size > maxSize) {
        throw new Error('File size exceeds 5MB limit.');
      }

      // Generate unique filename
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${user?.id}/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

      // Upload to storage
      const { data, error } = await supabase?.storage
        ?.from('review-photos')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Photo upload error:', error?.message);
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage
        ?.from('review-photos')
        ?.getPublicUrl(data?.path);

      return {
        path: data?.path,
        url: publicUrl,
        name: file?.name,
        size: file?.size,
        type: file?.type
      };
    } catch (error) {
      console.error('Upload review photo error:', error);
      throw error;
    }
  },

  // Delete review photo
  async deleteReviewPhoto(photoPath) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user owns the photo (path starts with user ID)
      if (!photoPath?.startsWith(`${user?.id}/`)) {
        throw new Error('Unauthorized to delete this photo');
      }

      const { error } = await supabase?.storage
        ?.from('review-photos')
        ?.remove([photoPath]);

      if (error) {
        console.error('Photo delete error:', error?.message);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Delete review photo error:', error);
      throw error;
    }
  },

  // Get public URL for review photo
  getReviewPhotoUrl(photoPath) {
    try {
      const { data: { publicUrl } } = supabase?.storage
        ?.from('review-photos')
        ?.getPublicUrl(photoPath);

      return publicUrl;
    } catch (error) {
      console.error('Get photo URL error:', error);
      return null;
    }
  },

  // Subscribe to real-time review updates for a user
  subscribeToUserReviews(userId, callback) {
    const channel = supabase
      ?.channel(`user_reviews_${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ratings',
          filter: `to_user_id=eq.${userId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  // Subscribe to real-time review response updates
  subscribeToReviewResponses(ratingId, callback) {
    const channel = supabase
      ?.channel(`review_responses_${ratingId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'review_responses',
          filter: `rating_id=eq.${ratingId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  // Unsubscribe from channel
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};